import type React from 'react';

import LabelValueList, { type LabelValue } from '@components/shared/labelValueList';
import { useGetResources } from '@hooks/useGetResources';
import { isLaeringsaktivitetCreatedByInnbygger } from '@utils/utils';

import type { OpprettetAvType } from '@src/types/minhelseentities';

import { shortDateNumbers } from '@helsenorge/core-utils/date-fns-utils';

interface VerktoyMetaInfoProps {
  begrunnelse?: string | null;
  opprettetAvType?: OpprettetAvType;
  opprettet?: string;
  opprettetAv?: string;
}

function formatDisplayDate(dateString?: string): string {
  if (!dateString) {
    return '';
  }
  return shortDateNumbers(new Date(dateString));
}

function createMetaInfoItems(
  opprettet: string | undefined,
  opprettetAvType: OpprettetAvType | undefined,
  opprettetAvNavn: string | undefined,
  resources: ReturnType<typeof useGetResources>,
  begrunnelse?: string | undefined
): LabelValue[] {
  const opprettetDato = formatDisplayDate(opprettet);
  if (isLaeringsaktivitetCreatedByInnbygger(opprettetAvType)) {
    const label = resources?.verktoy_lagt_til ?? '';
    const value = [resources?.verktoy_lagt_til_av_deg, opprettetDato].filter(Boolean).join(' ');
    return [{ label, value }];
  }

  const items: LabelValue[] = [];
  if (begrunnelse) {
    items.push({
      label: resources?.verktoy_begrunnelse_label ?? '',
      value: begrunnelse,
    });
  }

  const mottattFraParts = [opprettetAvNavn, opprettetDato].filter(Boolean);
  if (mottattFraParts.length) {
    items.push({
      label: resources?.mottatt_fra_label ?? '',
      value: mottattFraParts.join(', '),
    });
  }

  return items;
}

const VerktoyMetaInfo: React.FC<VerktoyMetaInfoProps> = ({ begrunnelse, opprettetAvType, opprettet, opprettetAv }) => {
  const resources = useGetResources();
  const items = createMetaInfoItems(opprettet, opprettetAvType, opprettetAv, resources, begrunnelse ?? undefined);
  if (items.length === 0) return null;
  return <LabelValueList items={items} />;
};

export default VerktoyMetaInfo;
